import type { ReactNode } from 'react';
import { useLoaderData, useSearchParams } from 'react-router';
import { useHydrated } from 'remix-utils';

import {
    MagicIsland,
    MagicIslandProvider,
} from '~/ui-rocco/pages/detail-shared';
import type { loader } from '~/routes/_app.project.$urlSegment._index';

interface ProjectDetailsMagicIslandProps {
    children: ReactNode;
}

/**
 * Project-detail wrapper for the Magic Island — the project counterpart to
 * `ProductDetailsMagicIsland`. Provides the controller context around the page
 * content (so sections can register their island modes) and mounts the shell
 * once hydrated. Gated by the `details_revamp_2026` umbrella flag, with a
 * `?detailsRevamp=1` dev override.
 *
 * When disabled it renders the children untouched and provides no context — the
 * section hooks then no-op, so the page is unaffected. The island is client-only
 * (`useHydrated`), so the server renders nothing and there is no hydration shift.
 */
export const ProjectDetailsMagicIsland = ({
    children,
}: ProjectDetailsMagicIslandProps) => {
    const { detailsRevamp } = useLoaderData<typeof loader>();
    const [searchParams] = useSearchParams();
    const hydrated = useHydrated();

    const enabled =
        detailsRevamp ||
        (import.meta.env.DEV && searchParams.get('detailsRevamp') === '1');

    if (!enabled) return <>{children}</>;

    return (
        <MagicIslandProvider>
            {children}
            {hydrated ? <MagicIsland /> : null}
        </MagicIslandProvider>
    );
};
